import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
	"cc-inline-flex cc-items-center cc-justify-center cc-whitespace-nowrap cc-text-sm cc-font-medium cc-ring-offset-white cc-transition-colors focus-visible:cc-outline-none focus-visible:cc-ring-2 focus-visible:cc-ring-gray-950 focus-visible:cc-ring-offset-2 disabled:cc-pointer-events-none disabled:cc-opacity-50",
	{
		variants: {
			variant: {
				default: "cc-bg-gray-800 cc-text-gray-50 hover:cc-bg-primary-900",
				destructive:
					"cc-bg-red-500 cc-text-gray-50 hover:cc-bg-red-500/90 ",
				outline:
					"cc-border cc-border-gray-200 cc-bg-white hover:cc-bg-gray-100 hover:cc-text-gray-900 ",
				secondary:
					"cc-bg-gray-100 cc-text-gray-900 hover:cc-bg-gray-100/80",
				ghost: "hover:cc-bg-gray-100 hover:cc-text-gray-900",
				link: "cc-text-gray-900 cc-underline-offset-4 hover:cc-underline",
			},
			size: {
				default: "cc-h-10 cc-px-4 cc-py-2",
				sm: "cc-h-9 cc-rounded-md cc-px-3",
				lg: "cc-h-11 cc-rounded-md cc-px-8",
				icon: "cc-h-10 cc-w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
	VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button"
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		)
	}
)
Button.displayName = "Button"

export { Button, buttonVariants }
